import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isPartialAuthActive, setPartialAuthActive } from '../auth/partialAuthStorage';
import { waitForSilentSso } from '../auth/preflightAuth';
import authService from '../services/authService';
import type { UserInfo } from '../types/auth';
import { isKeycloakLoginEnabled } from '../util/constants';
import { logger } from '../util/logger';
import { setAuthStateForAnalytics } from '../util/posthog';

export interface AuthContextType {
  isAuthenticated: boolean;
  isPartiallyAuthenticated: boolean;
  setPartialAuth: (value: boolean) => void;
  login: () => Promise<boolean>;
  logout: (isCompleteLogout?: boolean) => void;
  user: UserInfo | null;
  loading: boolean;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    authService.isAuthenticated()
  );
  const [isPartiallyAuthenticated, setIsPartiallyAuthenticated] = useState<boolean>(
    isPartialAuthActive()
  );
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      
      if (authService.isAuthenticated()) {
        try {
          const userInfo = await authService.getUserInfo();
          setUser(userInfo);
          setIsAuthenticated(true);
        } catch (error) {
          logger.error('Failed to get user info:', error);
          await authService.logout(false);
          setIsAuthenticated(false);
          setUser(null);
        }
      }

      if (isKeycloakLoginEnabled()) {
        await waitForSilentSso();
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    setAuthStateForAnalytics(isAuthenticated);
  }, [isAuthenticated]);

  const checkAuth = useCallback(async (): Promise<void> => {
    if (authService.isAuthenticated()) {
      const userInfo = await authService.getUserInfo();
      setUser(userInfo);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  /**
   * Set partial authentication state (true after login+OTP, before accepting terms).
   * Persisted in sessionStorage so a page refresh mid-flow doesn't break the T&C step.
   */
  const setPartialAuth = useCallback((value: boolean): void => {
    setPartialAuthActive(value);
    setIsPartiallyAuthenticated(value);
  }, []);

  const login = useCallback(async (): Promise<boolean> => {
    try {
      const userInfo = await authService.getUserInfo();
      setUser(userInfo);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      logger.error('Login failed:', error);
      return false;
    }
  }, []);

  const logout = useCallback(async (isCompleteLogout: boolean) => {
    await authService
      .logout(isCompleteLogout)
      .then(() => {
        setPartialAuthActive(false);
        setIsAuthenticated(false);
        setIsPartiallyAuthenticated(false);
        setUser(null);
        authService.customLogout();
      })
      .catch((error) => {
        logger.error('Logout failed:', error);
      });
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isPartiallyAuthenticated,
      setPartialAuth,
      login,
      logout: (isCompleteLogout = true) => logout(isCompleteLogout),
      user,
      loading,
      checkAuth,
    }),
    [isAuthenticated, isPartiallyAuthenticated, login, logout, user, loading, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
